import { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { 
  Shield, Clock, AlertTriangle, Ban, BarChart3,
  Lock, CheckCircle, Info, Heart
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const ResponsibleGamingPage = () => {
  const { user, token } = useContext(UserContext);
  const [limits, setLimits] = useState(null);
  const [exclusionStatus, setExclusionStatus] = useState(null);
  const [activitySummary, setActivitySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [newDailyLimit, setNewDailyLimit] = useState("");
  const [newWeeklyLimit, setNewWeeklyLimit] = useState("");
  const [newMonthlyLimit, setNewMonthlyLimit] = useState("");
  const [exclusionDays, setExclusionDays] = useState(7);
  const [cooldownHours, setCooldownHours] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [limitsRes, exclusionRes, activityRes] = await Promise.all([
        axios.get(`${API}/responsible-gaming/limits`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/responsible-gaming/self-exclusion/status`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/responsible-gaming/activity-summary`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setLimits(limitsRes.data);
      setExclusionStatus(exclusionRes.data);
      setActivitySummary(activityRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateLimits = async () => {
    try {
      const response = await axios.put(`${API}/responsible-gaming/limits`, {
        daily_limit: newDailyLimit ? parseFloat(newDailyLimit) : null,
        weekly_limit: newWeeklyLimit ? parseFloat(newWeeklyLimit) : null,
        monthly_limit: newMonthlyLimit ? parseFloat(newMonthlyLimit) : null
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update limits");
    }
  };

  const setSelfExclusion = async () => {
    if (!confirm(`Are you sure you want to self-exclude for ${exclusionDays === 0 ? 'permanently' : `${exclusionDays} days`}? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/responsible-gaming/self-exclusion`, {
        duration_days: exclusionDays
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set self-exclusion");
    }
  };

  const setCooldown = async () => {
    try {
      const response = await axios.post(`${API}/responsible-gaming/cooldown`, {
        hours: cooldownHours
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set cooldown");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007AFF] to-[#00E701] flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']">Responsible Gaming</h1>
            <p className="text-[#A1A1AA] text-sm">Tools to help you stay in control</p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="p-4 rounded-xl bg-[#007AFF]/10 border border-[#007AFF]/30">
          <div className="flex items-start gap-3">
            <Heart className="w-5 h-5 text-[#007AFF] mt-0.5" />
            <div>
              <p className="text-white font-medium">We care about your wellbeing</p>
              <p className="text-[#A1A1AA] text-sm mt-1">
                Gambling should be fun, not a problem. Use these tools to set limits, take breaks, 
                or self-exclude if needed. If you need help, contact support or visit{" "}
                <a href="https://www.begambleaware.org" target="_blank" rel="noopener" className="text-[#007AFF]">
                  BeGambleAware.org
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Activity Summary */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#FFD600]" />
            Your Activity Summary
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: "This Week", data: activitySummary?.weekly },
              { label: "This Month", data: activitySummary?.monthly },
              { label: "All Time", data: activitySummary?.all_time }
            ].map((period, i) => (
              <div key={i} className="p-4 rounded-xl bg-white/5">
                <p className="text-[#A1A1AA] text-sm mb-2">{period.label}</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/60">Bets</span>
                    <span className="text-white">{period.data?.bets || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Wagered</span>
                    <span className="text-white">₹{period.data?.wagered?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Net</span>
                    <span className={period.data?.net >= 0 ? 'text-[#00E701]' : 'text-[#FF3B30]'}>
                      {period.data?.net >= 0 ? '+' : ''}₹{period.data?.net?.toLocaleString() || 0}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Deposit Limits */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#00E701]" />
            Deposit Limits
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {[
              { label: "Daily Limit", key: "daily", current: limits?.limits?.daily_limit, used: limits?.current_deposits?.daily },
              { label: "Weekly Limit", key: "weekly", current: limits?.limits?.weekly_limit, used: limits?.current_deposits?.weekly },
              { label: "Monthly Limit", key: "monthly", current: limits?.limits?.monthly_limit, used: limits?.current_deposits?.monthly }
            ].map((limit, i) => (
              <div key={i} className="p-4 rounded-xl bg-white/5">
                <p className="text-[#A1A1AA] text-sm mb-2">{limit.label}</p>
                {limit.current ? (
                  <>
                    <p className="text-white font-bold">₹{limit.current.toLocaleString()}</p>
                    <Progress 
                      value={Math.min((limit.used / limit.current) * 100, 100)} 
                      className="h-2 mt-2" 
                    />
                    <p className="text-[#52525B] text-xs mt-1">
                      ₹{limit.used?.toLocaleString() || 0} / ₹{limit.current.toLocaleString()}
                    </p>
                  </>
                ) : (
                  <p className="text-[#52525B]">No limit set</p>
                )}
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              type="number"
              placeholder="Daily limit (₹)"
              value={newDailyLimit}
              onChange={(e) => setNewDailyLimit(e.target.value)}
              className="bg-black/20 border-white/10 text-white"
            />
            <Input
              type="number"
              placeholder="Weekly limit (₹)"
              value={newWeeklyLimit}
              onChange={(e) => setNewWeeklyLimit(e.target.value)}
              className="bg-black/20 border-white/10 text-white"
            />
            <Input
              type="number"
              placeholder="Monthly limit (₹)"
              value={newMonthlyLimit}
              onChange={(e) => setNewMonthlyLimit(e.target.value)}
              className="bg-black/20 border-white/10 text-white"
            />
          </div>
          <Button onClick={updateLimits} className="mt-4 bg-[#00E701] hover:bg-[#00C201] text-black">
            Update Limits
          </Button>
          <p className="text-[#52525B] text-xs mt-2">
            Note: Decreasing limits takes effect immediately. Increasing limits has a 24-hour waiting period.
          </p>
        </div>

        {/* Take a Break */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#FFD600]" />
            Take a Break
          </h3>
          
          <p className="text-[#A1A1AA] text-sm mb-4">
            Need a short break? Set a cooldown period to temporarily disable your account.
          </p>
          
          <div className="flex gap-2 mb-4">
            {[1, 6, 12, 24].map((hours) => (
              <Button
                key={hours}
                variant={cooldownHours === hours ? "default" : "outline"}
                onClick={() => setCooldownHours(hours)}
                className={cooldownHours === hours ? "bg-[#FFD600] text-black" : "border-white/20"}
              >
                {hours}h
              </Button>
            ))}
          </div>
          
          <Button onClick={setCooldown} className="bg-[#FFD600] hover:bg-[#E6C200] text-black">
            <Clock className="w-4 h-4 mr-2" />
            Activate {cooldownHours} Hour Cooldown
          </Button>
        </div>

        {/* Self Exclusion */}
        <div className="p-6 rounded-2xl bg-[#FF3B30]/10 border border-[#FF3B30]/30">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Ban className="w-5 h-5 text-[#FF3B30]" />
            Self-Exclusion
          </h3>
          
          {exclusionStatus?.active ? (
            <div className="p-4 rounded-xl bg-[#FF3B30]/20">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
                <span className="text-[#FF3B30] font-bold">Self-exclusion active</span>
              </div>
              <p className="text-white/70 mt-2">{exclusionStatus.message}</p>
            </div>
          ) : (
            <>
              <p className="text-[#A1A1AA] text-sm mb-4">
                Self-exclusion will block access to your account for the selected period. 
                This cannot be reversed early. Use this if you feel you need a longer break.
              </p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  { days: 1, label: "1 Day" },
                  { days: 7, label: "1 Week" },
                  { days: 30, label: "1 Month" },
                  { days: 90, label: "3 Months" },
                  { days: 365, label: "1 Year" },
                  { days: 0, label: "Permanent" }
                ].map((option) => (
                  <Button
                    key={option.days}
                    variant={exclusionDays === option.days ? "default" : "outline"}
                    onClick={() => setExclusionDays(option.days)}
                    className={exclusionDays === option.days ? "bg-[#FF3B30] text-white" : "border-white/20"}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              
              <Button 
                onClick={setSelfExclusion} 
                className="bg-[#FF3B30] hover:bg-[#CC2F26] text-white"
              >
                <Ban className="w-4 h-4 mr-2" />
                Activate Self-Exclusion
              </Button>
            </>
          )}
        </div>

        {/* Help Resources */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-[#007AFF]" />
            Help Resources
          </h3>
          
          <div className="space-y-3">
            <a 
              href="https://www.begambleaware.org" 
              target="_blank" 
              rel="noopener"
              className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <p className="text-white font-medium">BeGambleAware</p>
              <p className="text-[#A1A1AA] text-sm">Free, confidential support</p>
            </a>
            <a 
              href="https://www.gamcare.org.uk" 
              target="_blank" 
              rel="noopener"
              className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <p className="text-white font-medium">GamCare</p>
              <p className="text-[#A1A1AA] text-sm">Advice and support for problem gambling</p>
            </a>
            <a 
              href="https://www.gamblingtherapy.org" 
              target="_blank" 
              rel="noopener"
              className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <p className="text-white font-medium">Gambling Therapy</p>
              <p className="text-[#A1A1AA] text-sm">Free online support</p>
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};
